import { interpretSagaValidationResponse } from './lib/saga-validation.js';
export { interpretSagaValidationResponse } from './lib/saga-validation.js';
export const CLUB_AUTOCOMPLETE_MIN_QUERY_LENGTH = 2;
export const CLUB_AUTOCOMPLETE_DEBOUNCE_MS = 180;
export const CLUB_AUTOCOMPLETE_PAGE_SIZE = 8;
export const normalizeClubSearchResult = (result) => ({
    clubCode: result.ClubCode,
    name: result.Name,
    ...(result.countryCode ? { countryCode: result.countryCode } : {}),
    ...(result.countryName ? { countryName: result.countryName } : {}),
    ...(result.address ? { address: result.address } : {}),
    ...(typeof result.latitude === 'number' ? { latitude: result.latitude } : {}),
    ...(typeof result.longitude === 'number' ? { longitude: result.longitude } : {}),
    ...(result.mcaV1Region ? { mcaV1Region: result.mcaV1Region } : {}),
    ...(typeof result.useBridge === 'boolean' ? { useBridge: result.useBridge } : {}),
    ...(typeof result.useTeeSheet === 'boolean' ? { useTeeSheet: result.useTeeSheet } : {}),
});
export const isAbortError = (error) => {
    if (!error || typeof error !== 'object' || !('name' in error)) {
        return false;
    }
    return error.name === 'AbortError';
};
export class HttpRequestError extends Error {
    status;
    statusText;
    url;
    headers;
    bodyText;
    bodyJson;
    errorCode;
    constructor(message, meta) {
        super(message);
        this.status = meta.status;
        this.statusText = meta.statusText;
        this.url = meta.url;
        this.headers = meta.headers;
        this.bodyText = meta.bodyText;
        this.bodyJson = meta.bodyJson;
        this.errorCode = extractErrorCode(meta.bodyJson);
    }
}
const toHeaderRecord = (headers) => {
    const record = {};
    headers.forEach((value, key) => {
        record[key.toLowerCase()] = value;
    });
    return record;
};
const extractErrorCode = (bodyJson) => {
    if (!bodyJson || typeof bodyJson !== 'object') {
        return undefined;
    }
    const value = bodyJson.errorCode;
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};
const readErrorMeta = async (url, res) => {
    let bodyText;
    let bodyJson;
    try {
        bodyText = await res.text();
        if (bodyText) {
            try {
                bodyJson = JSON.parse(bodyText);
            }
            catch {
                bodyJson = undefined;
            }
        }
    }
    catch {
        bodyText = undefined;
    }
    return {
        status: res.status,
        statusText: res.statusText,
        url,
        headers: toHeaderRecord(res.headers),
        bodyText,
        bodyJson,
    };
};
const throwHttpError = async (url, res) => {
    const meta = await readErrorMeta(url, res);
    throw new HttpRequestError(`HTTP ${meta.status} ${meta.statusText} for ${meta.url}`, meta);
};
const toApiResponseMeta = (res, url) => ({
    status: res.status,
    statusText: res.statusText,
    url,
    headers: toHeaderRecord(res.headers),
});
const parseBooleanHeader = (value) => {
    if (value === 'true')
        return true;
    if (value === 'false')
        return false;
    return undefined;
};
const toBookingResponseMeta = (res, url) => {
    const meta = toApiResponseMeta(res, url);
    return {
        ...meta,
        bookingStatus: meta.headers['x-booking-status'],
        idempotent: parseBooleanHeader(meta.headers['x-idempotent']),
        requestId: meta.headers['x-request-id'],
        idempotencyKey: meta.headers['idempotency-key'],
    };
};
const readOptionalJson = async (res) => {
    if (res.status === 204) {
        return undefined;
    }
    const contentLength = res.headers.get('content-length');
    if (contentLength && Number(contentLength) === 0) {
        return undefined;
    }
    const text = await res.text();
    if (!text) {
        return undefined;
    }
    try {
        return JSON.parse(text);
    }
    catch {
        return undefined;
    }
};
const requestJsonWithMeta = async (path, init, headers, metaBuilder) => {
    const url = buildUrl(path);
    const mergedHeaders = buildHeaders(headers);
    if (init.body) {
        mergedHeaders.set('Content-Type', 'application/json');
    }
    const res = await fetch(url, {
        ...init,
        headers: mergedHeaders,
        credentials: 'include',
    });
    if (!res.ok)
        await throwHttpError(url, res);
    const data = await readOptionalJson(res);
    return { data, meta: metaBuilder(res, url) };
};
let runtimeAuthProvider;
export function setTeeTimeAuthTokenProvider(provider) {
    runtimeAuthProvider = provider;
}
let runtimeBaseOverride;
const buildHeaders = (headers) => {
    const merged = new Headers(headers ?? undefined);
    if (!merged.has('X-Requested-With')) {
        merged.set('X-Requested-With', 'XMLHttpRequest');
    }
    const token = runtimeAuthProvider?.();
    const trimmed = typeof token === 'string' ? token.trim() : '';
    if (trimmed && !merged.has('Authorization')) {
        merged.set('Authorization', `Bearer ${trimmed}`);
    }
    return merged;
};
const bearerHeaders = (accessToken) => {
    const trimmed = accessToken?.trim();
    return trimmed ? { Authorization: `Bearer ${trimmed}` } : undefined;
};
export function setTeeTimeBaseUrl(url) {
    runtimeBaseOverride = sanitizeBase(url ?? '');
}
function sanitizeBase(input) {
    if (!input)
        return '';
    let b = input.trim();
    b = b.replace(/\/?api\/docs\/?$/i, '');
    b = b.replace(/\/?docs\/?$/i, '');
    b = b.replace(/\/?api\/?$/i, '');
    return b.replace(/\/$/, '');
}
function baseUrl() {
    if (runtimeBaseOverride)
        return runtimeBaseOverride;
    const fromMeta = globalThis
        .importMetaEnv?.['VITE_TEE_TIME_BASE_URL'];
    const procEnv = globalThis.process?.env;
    const envVar = procEnv?.['VITE_TEE_TIME_BASE_URL'];
    const expoEnvVar = procEnv?.['EXPO_PUBLIC_TEE_TIME_API'];
    const b = sanitizeBase(fromMeta ?? envVar ?? expoEnvVar ?? '');
    return b;
}
function resolveApiPath(path) {
    if (!path.startsWith('/api/')) {
        return path;
    }
    if (/^\/api\/v\d+\//.test(path)) {
        return path;
    }
    // Leave version-neutral routes untouched (e.g., /api/clubs, /api/health, /api/ip-location).
    // Auto-prefixing to /api/v1 was causing 404s against unversioned backend controllers.
    return path;
}
function buildUrl(path) {
    return `${baseUrl()}${resolveApiPath(path)}`;
}
async function getJson(path, headers, init) {
    const url = buildUrl(path);
    const mergedHeaders = buildHeaders(headers);
    const res = await fetch(url, {
        method: 'GET',
        headers: mergedHeaders,
        credentials: 'include',
        signal: init?.signal,
    });
    if (!res.ok)
        await throwHttpError(url, res);
    return (await res.json());
}
async function delJson(path, headers, body) {
    const url = buildUrl(path);
    const mergedHeaders = buildHeaders(headers);
    mergedHeaders.set('Content-Type', 'application/json');
    const res = await fetch(url, {
        method: 'DELETE',
        headers: mergedHeaders,
        credentials: 'include',
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok)
        await throwHttpError(url, res);
    if (res.status === 204) {
        return undefined;
    }
    const contentLength = res.headers.get('content-length');
    if (contentLength && Number(contentLength) === 0) {
        return undefined;
    }
    const text = await res.text();
    if (!text) {
        return undefined;
    }
    try {
        return JSON.parse(text);
    }
    catch {
        return undefined;
    }
}
async function postJson(path, payload, headers, init) {
    const url = buildUrl(path);
    const mergedHeaders = buildHeaders(headers);
    mergedHeaders.set('Content-Type', 'application/json');
    const res = await fetch(url, {
        method: 'POST',
        headers: mergedHeaders,
        credentials: 'include',
        body: JSON.stringify(payload),
        signal: init?.signal,
    });
    if (!res.ok)
        await throwHttpError(url, res);
    return (await res.json());
}
async function postJsonWithMeta(path, payload, headers, init, metaBuilder) {
    return requestJsonWithMeta(path, {
        method: 'POST',
        body: JSON.stringify(payload),
        signal: init?.signal,
    }, headers, metaBuilder);
}
async function putJson(path, payload, headers, init) {
    const url = buildUrl(path);
    const mergedHeaders = buildHeaders(headers);
    mergedHeaders.set('Content-Type', 'application/json');
    const res = await fetch(url, {
        method: 'PUT',
        headers: mergedHeaders,
        credentials: 'include',
        body: JSON.stringify(payload),
        signal: init?.signal,
    });
    if (!res.ok)
        await throwHttpError(url, res);
    return (await res.json());
}
async function patchJson(path, payload, headers) {
    const url = buildUrl(path);
    const mergedHeaders = buildHeaders(headers);
    mergedHeaders.set('Content-Type', 'application/json');
    const res = await fetch(url, {
        method: 'PATCH',
        headers: mergedHeaders,
        credentials: 'include',
        body: JSON.stringify(payload),
    });
    if (!res.ok)
        await throwHttpError(url, res);
    return (await res.json());
}
async function patchJsonWithMeta(path, payload, headers, metaBuilder) {
    return requestJsonWithMeta(path, { method: 'PATCH', body: JSON.stringify(payload) }, headers, metaBuilder);
}
async function delJsonWithMeta(path, headers, body, metaBuilder) {
    return requestJsonWithMeta(path, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined }, headers, metaBuilder);
}
async function postForm(path, formData, headers) {
    const url = buildUrl(path);
    const mergedHeaders = buildHeaders(headers);
    const res = await fetch(url, {
        method: 'POST',
        headers: mergedHeaders,
        credentials: 'include',
        body: formData,
    });
    if (!res.ok)
        await throwHttpError(url, res);
    return (await res.json());
}
// Courses
export const fetchClubList = () => getJson('/api/mca-v1/clubs');
export const fetchClubDetails = (clubCode) => getJson(`/api/clubs/${encodeURIComponent(clubCode)}/details`);
export const fetchClubByCode = (clubCode, signal) => getJson(`/clubs?clubCode=${encodeURIComponent(clubCode)}`, undefined, {
    signal,
});
export const fetchClubById = (clubId, signal) => getJson(`/clubs?clubId=${encodeURIComponent(clubId)}`, undefined, {
    signal,
});
export const searchClubs = (options, signal) => {
    const params = new URLSearchParams();
    const nameQuery = options.query.trim();
    const codeQuery = options.code?.trim() ?? '';
    if (nameQuery.length > 0) {
        params.set('name', nameQuery);
    }
    if (codeQuery.length > 0) {
        params.set('code', codeQuery);
    }
    params.set('country', options.country?.trim().toUpperCase() || 'ALL');
    params.set('pageSize', String(options.pageSize ?? 8));
    return getJson(`/api/clubs/search?${params.toString()}`, undefined, {
        signal,
    });
};
export const fetchAvailableClubCountries = (signal) => getJson('/api/clubs/countries', undefined, { signal });
// IP Location (surfaced directly by TeeTime backend, not under mca-v1)
export const getIpLocation = (signal) => getJson('/api/ip-location', undefined, { signal });
export const getIpLocationDetails = (signal) => getJson('/api/ip-location/details', undefined, { signal });
const fetchIpLocationDiagnostic = async (endpoint, signal) => {
    const url = `${baseUrl()}${endpoint}`;
    try {
        const res = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            signal,
            headers: { Accept: 'application/json' },
        });
        const headers = toHeaderRecord(res.headers);
        const text = await res.text();
        let bodyJson;
        if (text) {
            try {
                bodyJson = JSON.parse(text);
            }
            catch {
                bodyJson = undefined;
            }
        }
        if (res.ok) {
            return {
                endpoint,
                ok: true,
                status: res.status,
                statusText: res.statusText,
                url,
                headers,
                bodyText: text || undefined,
                bodyJson,
                data: (bodyJson ?? undefined),
            };
        }
        return {
            endpoint,
            ok: false,
            status: res.status,
            statusText: res.statusText,
            url,
            headers,
            bodyText: text || undefined,
            bodyJson,
            errorMessage: text || res.statusText,
        };
    }
    catch (error) {
        if (error?.name === 'AbortError') {
            throw error;
        }
        const err = error;
        return {
            endpoint,
            ok: false,
            status: 0,
            statusText: 'NetworkError',
            url,
            headers: {},
            errorMessage: err.message,
        };
    }
};
export const diagnoseIpLocation = async (signal) => {
    const attempts = [];
    const detailed = await fetchIpLocationDiagnostic('/api/ip-location/details', signal);
    attempts.push(detailed);
    if (detailed.ok) {
        return { attempts, final: detailed };
    }
    const fallback = await fetchIpLocationDiagnostic('/api/ip-location', signal);
    attempts.push(fallback);
    return { attempts, final: fallback };
};
// Member Events
export const fetchAllMemberEvents = () => getJson('/api/mca-v1/member-events');
export const fetchClubEventDetails = (clubCode) => getJson(`/api/mca-v1/member-events/club?clubCode=${encodeURIComponent(clubCode)}`);
export const fetchMemberEvents = (memberId) => getJson(`/api/mca-v1/member-events/member?memberId=${encodeURIComponent(memberId)}`);
// Payments
export const checkPaymentStatus = (transactionRef, bookingId) => {
    const params = new URLSearchParams({ transactionRef });
    if (bookingId) {
        params.set('bookingId', bookingId);
    }
    return getJson(`/api/bookings/payment-status?${params.toString()}`);
};
// Player Bookings
export const getPlayerBookings = (email, status) => {
    const params = new URLSearchParams();
    if (status) {
        params.set('status', status);
    }
    const query = params.toString();
    return getJson(`/api/players/me/bookings${query ? `?${query}` : ''}`);
};
/**
 * Get the URL for downloading a booking receipt as PDF.
 * Opens in a new tab or can be used with window.open() for download.
 */
export const getBookingReceiptUrl = (bookingId) => `${baseUrl()}/api/players/me/bookings/${encodeURIComponent(bookingId)}/receipt`;
export const getPlayerCards = (email, clubcode) => getJson(`/api/mca-v1/player-cards?email=${encodeURIComponent(email)}${clubcode ? `&clubcode=${encodeURIComponent(clubcode)}` : ''}`);
export const getMyPlayerCards = (options) => {
    const clubCode = typeof options === 'string' ? options : options?.clubCode;
    return getJson(`/api/players/me/payment-cards${clubCode ? `?clubCode=${encodeURIComponent(clubCode)}` : ''}`, typeof options === 'string' ? undefined : bearerHeaders(options?.accessToken));
};
export const removePeachToken = (params) => delJson('/api/mca-v1/player-cards/peach-token', undefined, params);
export const removeMyPeachToken = (params, accessToken) => delJson('/api/players/me/payment-cards/peach-token', bearerHeaders(accessToken), params);
const buildPlayerSearchPath = (options = {}) => {
    const params = new URLSearchParams();
    if (options.q) {
        params.set('q', options.q);
    }
    if (Number.isFinite(options.limit) && options.limit) {
        params.set('limit', String(Math.trunc(options.limit)));
    }
    if (Number.isFinite(options.page) && options.page) {
        params.set('page', String(Math.trunc(options.page)));
    }
    const query = params.toString();
    return `/api/players${query ? `?${query}` : ''}`;
};
export const completePlayerRegistration = async () => {
    const target = '/api/players/complete-registration';
    const url = `${baseUrl()}${resolveApiPath(target)}`;
    const mergedHeaders = buildHeaders();
    mergedHeaders.set('Content-Type', 'application/json');
    const res = await fetch(url, {
        method: 'POST',
        headers: mergedHeaders,
        credentials: 'include',
        body: JSON.stringify({}),
    });
    if (!res.ok)
        await throwHttpError(url, res);
    return (await res.json());
};
export const searchPlayers = (options = {}) => getJson(buildPlayerSearchPath(options));
export const listPlayerFavorites = (playerId) => getJson(`/api/players/${encodeURIComponent(playerId)}/favorites`);
export const addPlayerFavorite = (playerId, clubId) => postJson(`/api/players/${encodeURIComponent(playerId)}/favorites`, { clubId });
export const removePlayerFavorite = (playerId, clubId) => delJson(`/api/players/${encodeURIComponent(playerId)}/favorites/${encodeURIComponent(clubId)}`);
/**
 * Favorites for the current authenticated player (resolved from JWT / session cookies).
 * These helpers use the `/players/me/favorites` endpoints and do not require a playerId parameter.
 */
export const listMyFavorites = () => getJson('/api/players/me/favorites');
export const addMyFavorite = (clubId) => postJson('/api/players/me/favorites', { clubId });
export const removeMyFavorite = (clubId) => delJson(`/api/players/me/favorites/${encodeURIComponent(clubId)}`);
export const listMyBuddies = () => getJson('/api/players/me/buddies');
/** @deprecated Direct UUID buddy add is disabled; use inviteMyBuddy with playerId, email, or phone. */
export const addMyBuddy = (_buddyId) => Promise.reject(new Error('Direct buddy add by Player UUID is disabled. Use inviteMyBuddy with playerId, email, or phone so the invitee can consent.'));
export const removeMyBuddy = (buddyId) => delJson(`/api/players/me/buddies/${encodeURIComponent(buddyId)}`);
export const inviteMyBuddy = (contact) => postJson('/api/players/me/buddies/invite', contact);
export const cancelMyBuddyInvite = (inviteId) => delJson(`/api/players/me/buddy-invites/${encodeURIComponent(inviteId)}`);
/**
 * Fetch buddy groupings for tee-sheet auto-fill.
 *
 * Despite the "groups" name, the server-side implementation is simply
 * `Array.splice(0, n)` over `[selfId, ...buddyIds]` — it chunks the
 * authenticated player's buddy list into slices of `size`. Order matches
 * the underlying buddy-insert order; there is no balancing or shuffling.
 *
 * `pad` is a literal string sentinel (typical value `'OPEN'`) used to fill
 * trailing slots when the last chunk is shorter than `size`. Pass it only
 * when the caller actually wants those sentinel strings in the response.
 */
export const fetchBuddyGroups = (size, pad) => {
    const params = new URLSearchParams();
    if (size !== undefined)
        params.set('size', String(size));
    if (pad)
        params.set('pad', pad);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return getJson(`/api/players/me/buddies/groups${qs}`);
};
export const listMyBuddyGroups = () => getJson('/api/players/me/buddy-groups');
export const createMyBuddyGroup = (request) => postJson('/api/players/me/buddy-groups', request);
export const updateMyBuddyGroup = (groupId, request) => patchJson(`/api/players/me/buddy-groups/${encodeURIComponent(groupId)}`, request);
export const deleteMyBuddyGroup = (groupId) => delJson(`/api/players/me/buddy-groups/${encodeURIComponent(groupId)}`);
// Tsheet
export const fetchTsheet = (req) => getJson(`/api/mca-v1/tsheet?date=${encodeURIComponent(req.date)}&clubcode=${encodeURIComponent(req.clubcode)}`);
// Saga
export const fetchSagaValidation = async (saganumber, name, surname) => {
    const response = await getJson(`/api/mca-v1/saga/validate?saganumber=${encodeURIComponent(saganumber)}&name=${encodeURIComponent(name)}&surname=${encodeURIComponent(surname)}`);
    return interpretSagaValidationResponse(response);
};
/* ------------------------------------------------------------------
 * Admin reports
 * -----------------------------------------------------------------*/
function buildAdminReportPath(path, options = {}) {
    const params = new URLSearchParams();
    if (Number.isFinite(options.rangeDays) && options.rangeDays && options.rangeDays > 0) {
        params.set('rangeDays', String(Math.trunc(options.rangeDays)));
    }
    if (options.courseId) {
        params.set('courseId', options.courseId);
    }
    if (options.clubId) {
        params.set('clubId', options.clubId);
    }
    const query = params.toString();
    return query ? `${path}?${query}` : path;
}
export const adminGetReportSummary = (options = {}) => {
    const path = buildAdminReportPath('/api/admin/reports/summary', options);
    return getJson(path, options.headers, { signal: options.signal });
};
export const adminListReportBreakdowns = (options = {}) => {
    const path = buildAdminReportPath('/api/admin/reports/breakdowns', options);
    return getJson(path, options.headers, { signal: options.signal });
};
export const adminGetOpsStatus = (options = {}) => getJson('/api/admin/ops/status', undefined, { signal: options.signal });
export const adminGetBuildInfo = (options = {}) => getJson('/api/admin/build-info', undefined, { signal: options.signal });
export const adminGetTenantProfile = (options = {}) => getJson('/api/admin/tenant/profile', options.headers, { signal: options.signal });
export const adminGetPlatformOutboxHealth = (options = {}) => getJson('/api/outbox/platform/health', undefined, { signal: options.signal });
export const adminGetPlatformOutboxMetrics = (options = {}) => getJson('/api/outbox/platform/metrics', undefined, { signal: options.signal });
export const adminListPlatformOutboxEntries = (params = {}) => {
    const query = new URLSearchParams();
    if (params.status)
        query.set('status', params.status);
    if (params.eventType)
        query.set('eventType', params.eventType);
    if (params.tenantId)
        query.set('tenantId', params.tenantId);
    if (params.limit !== undefined)
        query.set('limit', String(params.limit));
    if (params.offset !== undefined)
        query.set('offset', String(params.offset));
    const qs = query.toString();
    return getJson(`/api/outbox/platform/entries${qs ? `?${qs}` : ''}`, undefined, {
        signal: params.signal,
    });
};
export const adminGetPlatformOutboxEntry = (id, options = {}) => getJson(`/api/outbox/platform/entries/${encodeURIComponent(id)}`, undefined, {
    signal: options.signal,
});
export const adminRequeuePlatformOutboxEntry = (id, options = {}) => postJson(`/api/outbox/platform/entries/${encodeURIComponent(id)}/requeue`, {}, undefined, {
    signal: options.signal,
});
export const adminRequeuePlatformOutboxEntries = (ids, options = {}) => postJson('/api/outbox/platform/entries/requeue-many', { ids }, undefined, {
    signal: options.signal,
});
export const adminPurgePlatformOutbox = (olderThanDays = 7, options = {}) => postJson(`/api/outbox/platform/purge?olderThanDays=${olderThanDays}`, {}, undefined, {
    signal: options.signal,
});
/* ------------------------------------------------------------------
 * Health (TeeTime backend)
 * -----------------------------------------------------------------*/
export const getApiLiveness = () => getJson('/api/health/liveness');
export const getApiReadiness = () => getJson('/api/health/readiness');
// Tee Time Availability
export const fetchAvailableVisitorGolf = () => getJson('/api/mca-v1/visitor-golf');
// Backwards-compatible alias used by some UI hooks
export const fetchAvailableVisitorGolfWeb = () => fetchAvailableVisitorGolf();
export const fetchAvailableVisitorGolfByDate = (date, preferences) => getJson(`/api/mca-v1/visitor-golf/by-date?date=${encodeURIComponent(date)}&numberOfHoles=${encodeURIComponent(String(preferences.numberOfHoles))}&timeOfDay=${encodeURIComponent(preferences.timeOfDay)}&players=${encodeURIComponent(String(preferences.players ?? 0))}`);
export const checkClubVisitorGolfAvailability = (clubcode) => getJson(`/api/mca-v1/visitor-golf/club-availability?clubCode=${encodeURIComponent(clubcode)}`);
export const fetchAvailableVisitorTeeTimesByDate = (clubCode, date, preferences) => getJson(`/api/mca-v1/tee-times/visitor/by-date?clubCode=${encodeURIComponent(clubCode)}&date=${encodeURIComponent(date)}&numberOfHoles=${encodeURIComponent(String(preferences.numberOfHoles))}&timeOfDay=${encodeURIComponent(preferences.timeOfDay)}&players=${encodeURIComponent(String(preferences.players ?? 0))}`);
export const checkClubVisitorTeeTimeAvailability = (clubCode) => getJson(`/api/mca-v1/tee-times/club-availability?clubCode=${encodeURIComponent(clubCode)}`);
export const fetchVisitorTeeTimesByClubCode = (clubCode, options) => {
    const params = new URLSearchParams();
    params.set('date', options.date);
    if (options.courseCode)
        params.set('courseCode', options.courseCode);
    if (options.numPlayers !== undefined)
        params.set('numPlayers', String(options.numPlayers));
    if (options.nineHoles !== undefined)
        params.set('nineHoles', String(options.nineHoles));
    if (options.filterOutPast !== undefined)
        params.set('filterOutPast', String(options.filterOutPast));
    if (options.showPlayerDetails !== undefined)
        params.set('showPlayerDetails', String(options.showPlayerDetails));
    if (options.isAdmin !== undefined)
        params.set('isAdmin', String(options.isAdmin));
    return getJson(`/api/clubs/${encodeURIComponent(clubCode)}/tee-times/visitor?${params.toString()}`, undefined, { signal: options.signal }).then((res) => {
        // Normalize server response casing for clubCode
        return {
            ...res,
            clubCode: res.clubCode ?? res.clubcode,
        };
    });
};
const toStringValue = (value) => typeof value === 'string' ? value : value == null ? '' : String(value);
const toTrimmedString = (value) => toStringValue(value).trim();
const toNumberValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};
const normalizeOffer = (offer) => ({
    dateLabel: toTrimmedString(offer.Date),
    timeLabel: toTrimmedString(offer.Time),
    holeNumber: toTrimmedString(offer.HoleNumber),
    currency: toTrimmedString(offer.Currency) || 'R',
    visitorPrice: toNumberValue(offer.VisitorPrice),
    memberPrice: toNumberValue(offer.MemberPrice),
    teeTimeUid: toTrimmedString(offer.teetimeUID) ||
        toTrimmedString(offer.TeeTimeUID) ||
        toTrimmedString(offer.teeTimeUID) ||
        toTrimmedString(offer.uuid) ||
        toTrimmedString(offer.code) ||
        toTrimmedString(offer.TeeTimeCode),
    rateName: toTrimmedString(offer.RateName),
    slotsAvailable: toNumberValue(offer.SlotsAvailable),
});
const normalizeClubAvailability = (club) => ({
    clubCode: toTrimmedString(club.ClubCode),
    clubName: toTrimmedString(club.ClubName),
    courseCode: toTrimmedString(club.CourseCode),
    courseName: toTrimmedString(club.CourseName),
    currency: toTrimmedString(club.Currency) || 'R',
    message: toTrimmedString(club.Message),
    times: (club.Times ?? []).map(normalizeOffer),
});
const buildSpecialsPayload = (options) => {
    const clubCode = options.clubCode ?? '';
    const date = options.date;
    const numberPlayers = options.numberPlayers ?? 1;
    const sagaNumber = options.sagaNumber ?? '';
    const getNew = options.getNew ?? false;
    return {
        clubCode,
        date,
        timeOfDay: options.timeOfDay ?? 'all',
        numberOfHoles: options.numberOfHoles ?? 18,
        numberPlayers,
        numberOfTeeTimes: options.numberOfTeeTimes ?? 0,
        sagaNumber,
        getNew,
    };
};
export const fetchSpecialTeeTimeSpecials = async (options) => {
    if (!options?.date) {
        throw new Error('date is required to fetch tee-time specials');
    }
    const raw = await postJson('/api/mca-v1/tee-times/specials', buildSpecialsPayload(options), undefined, { signal: options.signal });
    return {
        message: toTrimmedString(raw?.Message),
        clubs: (raw?.ClubTimesAvailable ?? []).map(normalizeClubAvailability),
    };
};
export const fetchTeeTimePricing = (clubCode, date, preferences, numberOfTeetimes = 0, sagaNumber = '', getNew) => getJson(`/api/mca-v1/tee-times/pricing?clubCode=${encodeURIComponent(clubCode)}&date=${encodeURIComponent(date)}&numberOfHoles=${encodeURIComponent(String(preferences.numberOfHoles))}&timeOfDay=${encodeURIComponent(preferences.timeOfDay)}&players=${encodeURIComponent(String(preferences.players ?? 0))}&numberOfTeetimes=${encodeURIComponent(String(numberOfTeetimes))}&sagaNumber=${encodeURIComponent(sagaNumber)}&getNew=${getNew ? 'true' : 'false'}`);
export const fetchTeeTimePricingBatch = (clubCodes, date, preferences, options = {}) => {
    const params = new URLSearchParams();
    clubCodes.forEach((code) => {
        if (code) {
            params.append('clubCodes', code);
        }
    });
    params.set('date', date);
    params.set('numberOfHoles', String(preferences.numberOfHoles ?? 18));
    params.set('timeOfDay', preferences.timeOfDay ?? 'all');
    params.set('players', String(preferences.players ?? 0));
    if (options.numberOfTeetimes !== undefined) {
        params.set('numberOfTeetimes', String(options.numberOfTeetimes));
    }
    if (options.sagaNumber) {
        params.set('sagaNumber', options.sagaNumber);
    }
    if (options.getNew !== undefined) {
        params.set('getNew', options.getNew ? 'true' : 'false');
    }
    return getJson(`/api/mca-v1/tee-times/pricing/batch?${params.toString()}`);
};
export async function* streamTeeTimePricingBatch(clubCodes, date, preferences, options = {}) {
    const base = runtimeBaseOverride || '';
    const url = new URL((base || '') + '/api/mca-v1/tee-times/pricing/batch/stream', base || window.location.origin);
    const params = new URLSearchParams();
    for (const c of clubCodes)
        params.append('clubCodes', c);
    params.set('date', preferences.date);
    params.set('numberOfHoles', String(preferences.numberOfHoles));
    params.set('timeOfDay', preferences.timeOfDay);
    if (typeof preferences.players === 'number')
        params.set('players', String(preferences.players));
    const { concurrency, signal, ...requestOptions } = options;
    if (typeof requestOptions.numberOfTeetimes === 'number')
        params.set('numberOfTeetimes', String(requestOptions.numberOfTeetimes));
    if (requestOptions.sagaNumber)
        params.set('sagaNumber', requestOptions.sagaNumber);
    if (typeof requestOptions.getNew === 'boolean')
        params.set('getNew', requestOptions.getNew ? 'true' : 'false');
    if (typeof concurrency === 'number')
        params.set('concurrency', String(concurrency));
    url.search = params.toString();
    const res = await fetch(url.toString(), {
        credentials: 'include',
        headers: { Accept: 'application/x-ndjson' },
        signal,
    });
    if (!res.ok || !('body' in res) || !res.body) {
        throw new HttpRequestError('Streaming request failed', await readErrorMeta(url.toString(), res));
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done)
            break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n')) >= 0) {
            const line = buffer.slice(0, idx).trim();
            buffer = buffer.slice(idx + 1);
            if (!line)
                continue;
            try {
                const obj = JSON.parse(line);
                yield obj;
            }
            catch {
                // ignore malformed lines
            }
        }
    }
    if (buffer.trim()) {
        try {
            yield JSON.parse(buffer);
        }
        catch {
            /* ignore */
        }
    }
}
export const fetchNearbyClubCodes = (latitude, longitude, radiusKm) => getJson(`/clubs/nearby?lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}&radius=${encodeURIComponent(radiusKm)}`);
const parseSseEvent = (raw) => {
    const lines = raw
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
    if (lines.length === 0)
        return null;
    let eventType = 'message';
    const dataLines = [];
    let id;
    let retry;
    for (const line of lines) {
        if (line.startsWith('event:')) {
            eventType = line.slice('event:'.length).trim() || 'message';
        }
        else if (line.startsWith('data:')) {
            dataLines.push(line.slice('data:'.length).trimStart());
        }
        else if (line.startsWith('id:')) {
            id = line.slice('id:'.length).trim();
        }
        else if (line.startsWith('retry:')) {
            const parsed = Number(line.slice('retry:'.length).trim());
            retry = Number.isFinite(parsed) ? parsed : undefined;
        }
    }
    const dataText = dataLines.join('\n');
    let parsedData;
    if (dataText) {
        try {
            parsedData = JSON.parse(dataText);
        }
        catch {
            parsedData = dataText;
        }
    }
    if (eventType === 'heartbeat') {
        return { type: 'heartbeat', id, retry, data: parsedData };
    }
    if (eventType === 'complete') {
        return { type: 'complete', id, retry, data: parsedData };
    }
    if (eventType === 'ready' ||
        (parsedData &&
            typeof parsedData === 'object' &&
            'status' in parsedData &&
            parsedData['status'] === 'ready')) {
        return { type: 'ready', id, retry, data: parsedData };
    }
    return { type: 'data', id, retry, data: parsedData ?? null };
};
export async function* streamClubTeeSheets(options) {
    if (!options?.date) {
        throw new Error('date is required for tee-sheet streaming');
    }
    const base = runtimeBaseOverride || '';
    const url = new URL((base || '') + '/api/clubs/tee-sheet/stream', base || window.location.origin);
    const params = new URLSearchParams();
    if (options.clubCodes?.length) {
        params.set('clubs', options.clubCodes.join(','));
    }
    params.set('date', options.date);
    params.set('progressive', options.progressive === false ? 'false' : 'true');
    if (options.progressiveOrder) {
        params.set('progressiveOrder', options.progressiveOrder);
    }
    if (options.timeOfDay) {
        params.set('timeOfDay', options.timeOfDay);
    }
    if (options.includeClubMeta) {
        params.set('includeClubMeta', 'true');
    }
    if (options.specialsOnly) {
        params.set('specialsOnly', 'true');
    }
    if (options.favoriteClubs?.length) {
        params.set('favoriteClubs', options.favoriteClubs.join(','));
    }
    if (options.memberClubs?.length) {
        params.set('memberClubs', options.memberClubs.join(','));
    }
    if (options.country) {
        params.set('country', options.country);
    }
    if (options.location?.latitude !== undefined && options.location?.longitude !== undefined) {
        params.set('userLat', String(options.location.latitude));
        params.set('userLon', String(options.location.longitude));
        if (options.location.radiusKm !== undefined) {
            params.set('radiusKm', String(options.location.radiusKm));
        }
    }
    if (options.pricingMode) {
        params.set('pricingMode', options.pricingMode);
    }
    if (options.numberOfHoles) {
        params.set('numberOfHoles', String(options.numberOfHoles));
    }
    if (options.players !== undefined) {
        params.set('players', String(options.players));
    }
    if (options.keepAlive) {
        params.set('keepAlive', 'true');
    }
    url.search = params.toString();
    const res = await fetch(url.toString(), {
        credentials: 'include',
        headers: { Accept: 'text/event-stream' },
        signal: options.signal,
    });
    if (!res.ok || !res.body) {
        throw new HttpRequestError('Streaming request failed', await readErrorMeta(url.toString(), res));
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            let boundary;
            while ((boundary = buffer.indexOf('\n\n')) >= 0) {
                const rawEvent = buffer.slice(0, boundary);
                buffer = buffer.slice(boundary + 2);
                const parsed = parseSseEvent(rawEvent);
                if (parsed) {
                    yield parsed;
                }
            }
        }
    }
    finally {
        try {
            await reader.cancel();
        }
        catch {
            // The stream may already be closed by the server.
        }
        reader.releaseLock();
    }
    const finalEvent = buffer.trim();
    if (finalEvent) {
        const parsed = parseSseEvent(finalEvent);
        if (parsed) {
            yield parsed;
        }
    }
}
export const fetchClubTeeTimePricing = (clubCode, date, preferences, numberOfTeetimes = 0, sagaNumber = '', getNew) => getJson(`/api/mca-v1/tee-times/pricing/club?clubCode=${encodeURIComponent(clubCode)}&date=${encodeURIComponent(date)}&numberOfHoles=${encodeURIComponent(String(preferences.numberOfHoles))}&timeOfDay=${encodeURIComponent(preferences.timeOfDay)}&players=${encodeURIComponent(String(preferences.players ?? 0))}&numberOfTeetimes=${encodeURIComponent(String(numberOfTeetimes))}&sagaNumber=${encodeURIComponent(sagaNumber)}&getNew=${getNew ? 'true' : 'false'}`);
// Some frontends call the *_Web naming — make it alias to the same backend proxy
export const fetchTeeTimePricingWeb = (clubCode, date, preferences, numberOfTeetimes = 0, sagaNumber = '', getNew = false) => fetchTeeTimePricing(clubCode, date, preferences, numberOfTeetimes, sagaNumber, getNew);
// Bookings
/**
 * @deprecated Use holdBooking + confirmBooking with tee-sheet flow instead.
 * This legacy MCA endpoint will be removed in a future release.
 */
export const bookGolfTeeTime = (bookingRequest) => postJson('/api/mca-v1/golf-booking', bookingRequest);
/**
 * @deprecated Use initBookingPayment with tee-sheet hold/confirm flow instead.
 * This legacy MCA endpoint will be removed in a future release.
 */
export const testGolfBooking = (bookingRequest) => postJson('/api/mca-v1/test-golf-booking', bookingRequest);
export const initBookingPayment = (bookingRequest) => postJson('/api/bookings/payment-init', bookingRequest);
export const initBookingPaymentWithMeta = (bookingRequest) => postJsonWithMeta('/api/bookings/payment-init', bookingRequest, undefined, undefined, toBookingResponseMeta);
/**
 * POST /api/bookings/payment-init — multi-PSP checkout init.
 *
 * Same URL as the legacy `initBookingPayment` above (the new lib-first
 * `BookingController` in `libs/teetime-marketplace` replaced the old
 * controller in place). The runtime response is already this narrower
 * `PaymentInitResponse` shape — what changes here is the *typed* response,
 * which lets us drop the dead `peachDataWithToken` / `iveriDataWithToken`
 * branching in callers and surface either the hosted `paymentUrl` set or the
 * Peach `embeddedPayment` session returned by the backend.
 *
 * Use this for new payment flows. The legacy `initBookingPayment` (above)
 * still types the response as the wider `BookingResponseDto` for callers
 * that haven't migrated yet (currently the teetime web app's
 * `BookingDetailsModal.tsx`).
 */
export const initBookingHostedCheckout = (bookingRequest) => postJson('/api/bookings/payment-init', bookingRequest);
// Tee-sheet bookings (v1 API)
export const holdBooking = (payload, options = {}) => {
    const headers = {};
    const requestPayload = { ...payload };
    if (options.bookingChannel) {
        headers['X-Booking-Channel'] = options.bookingChannel;
        requestPayload.bookingChannel = options.bookingChannel;
    }
    if (options.idempotencyKey) {
        headers['Idempotency-Key'] = options.idempotencyKey;
    }
    if (options.requestId) {
        headers['X-Request-Id'] = options.requestId;
    }
    return postJson('/api/bookings/hold', requestPayload, headers, { signal: options.signal });
};
export const holdBookingWithMeta = (payload, options = {}) => {
    const headers = {};
    const requestPayload = { ...payload };
    if (options.bookingChannel) {
        headers['X-Booking-Channel'] = options.bookingChannel;
        requestPayload.bookingChannel = options.bookingChannel;
    }
    if (options.idempotencyKey) {
        headers['Idempotency-Key'] = options.idempotencyKey;
    }
    if (options.requestId) {
        headers['X-Request-Id'] = options.requestId;
    }
    return postJsonWithMeta('/api/bookings/hold', requestPayload, headers, { signal: options.signal }, toBookingResponseMeta);
};
export const confirmBooking = (bookingId, payload, options = {}) => {
    const id = String(bookingId ?? '').trim();
    if (!id) {
        throw new Error('bookingId is required');
    }
    const headers = {};
    if (options.requestId) {
        headers['X-Request-Id'] = options.requestId;
    }
    return postJson(`/api/bookings/${encodeURIComponent(id)}/confirm`, payload, headers, {
        signal: options.signal,
    });
};
export const confirmBookingWithMeta = (bookingId, payload, options = {}) => {
    const id = String(bookingId ?? '').trim();
    if (!id) {
        throw new Error('bookingId is required');
    }
    const headers = {};
    if (options.requestId) {
        headers['X-Request-Id'] = options.requestId;
    }
    return postJsonWithMeta(`/api/bookings/${encodeURIComponent(id)}/confirm`, payload, headers, { signal: options.signal }, toBookingResponseMeta);
};
/**
 * Cancel an existing booking.
 * @param bookingId - The booking ID to cancel
 * @param options - Optional player type and abort signal
 */
export const cancelBooking = (bookingId, options = {}) => {
    const params = new URLSearchParams();
    if (options.playerType) {
        params.set('playerType', options.playerType);
    }
    const query = params.toString();
    const path = `/api/bookings/${encodeURIComponent(bookingId)}${query ? `?${query}` : ''}`;
    return delJson(path);
};
export const cancelBookingWithMeta = (bookingId, options = {}) => {
    const params = new URLSearchParams();
    if (options.playerType) {
        params.set('playerType', options.playerType);
    }
    const query = params.toString();
    const path = `/api/bookings/${encodeURIComponent(bookingId)}${query ? `?${query}` : ''}`;
    return delJsonWithMeta(path, undefined, undefined, toBookingResponseMeta);
};
export const getWaitlistOffer = (courseId, offerId, options = {}) => getJson(`/api/admin/courses/${encodeURIComponent(courseId)}/waitlist/offers/${encodeURIComponent(offerId)}`, undefined, { signal: options.signal });
export const acceptWaitlistOffer = (courseId, offerId, options = {}) => postJson(`/api/admin/courses/${encodeURIComponent(courseId)}/waitlist/offers/${encodeURIComponent(offerId)}/accept`, {}, undefined, { signal: options.signal });
export const declineWaitlistOffer = (courseId, offerId, options = {}) => postJson(`/api/admin/courses/${encodeURIComponent(courseId)}/waitlist/offers/${encodeURIComponent(offerId)}/decline`, {}, undefined, { signal: options.signal });
// =============================================================================
// Waitlist Offers (public)
// =============================================================================
export const getWaitlistOfferPublic = (offerId, options = {}) => getJson(`/api/waitlist/offers/${encodeURIComponent(offerId)}`, undefined, {
    signal: options.signal,
});
export const acceptWaitlistOfferPublic = (offerId, options = {}) => postJson(`/api/waitlist/offers/${encodeURIComponent(offerId)}/accept`, {}, undefined, { signal: options.signal });
export const declineWaitlistOfferPublic = (offerId, options = {}) => postJson(`/api/waitlist/offers/${encodeURIComponent(offerId)}/decline`, {}, undefined, { signal: options.signal });
export const getMyWaitlist = (options = {}) => getJson('/api/v1/players/me/waitlist', undefined, { signal: options.signal });
export const leaveMyWaitlist = (entryId) => delJson(`/api/v1/players/me/waitlist/${encodeURIComponent(entryId)}`);
// Player Rate selection
export const fetchPlayerRateSelection = (teetimeUID, numberOfHoles, numberplayers, saganumber = '') => getJson(`/api/mca-v1/tee-times/player-rate-selection?teetimeUID=${encodeURIComponent(teetimeUID)}&numberOfHoles=${encodeURIComponent(String(numberOfHoles))}&numberplayers=${encodeURIComponent(String(numberplayers))}&saganumber=${encodeURIComponent(saganumber)}`);
/* ------------------------------------------------------------------
 * Admin – Clubs (TeeTime backend)
 * -----------------------------------------------------------------*/
export const adminListClubs = (options = {}) => getJson('/api/admin/clubs', options.headers);
export const adminGetClub = (id, options = {}) => getJson(`/api/admin/clubs/${encodeURIComponent(id)}`, options.headers);
export const adminCreateClub = (data, options = {}) => postJson('/api/admin/clubs', data, options.headers);
export const adminUpdateClub = (id, data, options = {}) => patchJson(`/api/admin/clubs/${encodeURIComponent(id)}`, data, options.headers);
function buildAssetPath(id, kind) {
    return `/api/admin/clubs/${encodeURIComponent(String(id))}/${kind}`;
}
export const adminUploadClubAsset = (id, file, kind, fieldName = 'file') => {
    const fd = new FormData();
    // For browser: File or Blob; for SSR tests, Blob is fine.
    fd.append(fieldName, file);
    return postForm(buildAssetPath(id, kind), fd);
};
export const adminUploadClubLogo = (id, file) => adminUploadClubAsset(id, file, 'logo');
export const adminUploadClubHeader = (id, file) => adminUploadClubAsset(id, file, 'header');
export const adminUploadClubFooter = (id, file) => adminUploadClubAsset(id, file, 'footer');
export const adminUploadClubBackground = (id, file) => adminUploadClubAsset(id, file, 'background');
const buildAdminMarketingCampaignQuery = (options) => {
    const params = new URLSearchParams({ tenantId: options.tenantId });
    if (options.placement)
        params.set('placement', options.placement);
    if (options.clubId)
        params.set('clubId', options.clubId);
    if (options.enabled !== undefined)
        params.set('enabled', String(options.enabled));
    if (options.skip !== undefined)
        params.set('skip', String(options.skip));
    if (options.take !== undefined)
        params.set('take', String(options.take));
    return params.toString();
};
export const adminListMarketingCampaigns = (options) => getJson(`/api/admin/marketing/campaigns?${buildAdminMarketingCampaignQuery(options)}`, options.headers, { signal: options.signal });
export const adminGetMarketingCampaign = (tenantId, campaignId, options = {}) => getJson(`/api/admin/marketing/campaigns/${encodeURIComponent(campaignId)}?tenantId=${encodeURIComponent(tenantId)}`, options.headers, { signal: options.signal });
export const adminCreateMarketingCampaign = (data) => postJson('/api/admin/marketing/campaigns', data);
export const adminUpdateMarketingCampaign = (tenantId, campaignId, data) => patchJson(`/api/admin/marketing/campaigns/${encodeURIComponent(campaignId)}?tenantId=${encodeURIComponent(tenantId)}`, data);
export const adminDeleteMarketingCampaign = (tenantId, campaignId) => delJson(`/api/admin/marketing/campaigns/${encodeURIComponent(campaignId)}?tenantId=${encodeURIComponent(tenantId)}`);
export const adminCreateMarketingBannerUploadUrl = (data) => postJson('/api/admin/marketing/campaigns/banner-upload-url', data);
export const adminGetMarketingCampaignStats = (campaignId, options) => {
    const params = new URLSearchParams({ tenantId: options.tenantId });
    if (options.from)
        params.set('from', options.from);
    if (options.to)
        params.set('to', options.to);
    return getJson(`/api/admin/marketing/campaigns/${encodeURIComponent(campaignId)}/stats?${params.toString()}`, options.headers, { signal: options.signal });
};
const buildPublicMarketingCampaignQuery = (options) => {
    const params = new URLSearchParams({
        tenantId: options.tenantId,
        placement: options.placement,
    });
    if (options.clubId)
        params.set('clubId', options.clubId);
    return params.toString();
};
export const fetchPublicMarketingCampaigns = (options) => {
    const url = buildUrl(`/api/marketing/campaigns?${buildPublicMarketingCampaignQuery(options)}`);
    const headers = new Headers();
    headers.set('X-Requested-With', 'XMLHttpRequest');
    return fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: options.signal,
    }).then(async (res) => {
        if (!res.ok)
            await throwHttpError(url, res);
        return (await res.json());
    });
};
export const recordPublicMarketingCampaignImpression = (tenantId, campaignId) => {
    const url = buildUrl(`/api/marketing/campaigns/${encodeURIComponent(campaignId)}/impression`);
    const headers = new Headers({ 'Content-Type': 'application/json' });
    headers.set('X-Requested-With', 'XMLHttpRequest');
    return fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ tenantId }),
        keepalive: true,
    }).then(async (res) => {
        if (!res.ok)
            await throwHttpError(url, res);
        return (await res.json());
    });
};
const buildAdminBookingsPath = (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.clubId)
        searchParams.set('clubId', params.clubId);
    if (params.courseId)
        searchParams.set('courseId', params.courseId);
    if (params.playerId)
        searchParams.set('playerId', params.playerId);
    if (params.teeDateFrom)
        searchParams.set('teeDateFrom', params.teeDateFrom);
    if (params.teeDateTo)
        searchParams.set('teeDateTo', params.teeDateTo);
    if (params.bookingChannel) {
        const channels = Array.isArray(params.bookingChannel)
            ? params.bookingChannel
            : [params.bookingChannel];
        const normalized = channels.map((channel) => channel.trim()).filter(Boolean);
        if (normalized.length > 0) {
            searchParams.set('bookingChannel', normalized.join(','));
        }
    }
    if (params.memberNumber)
        searchParams.set('memberNumber', params.memberNumber);
    if (params.playerName)
        searchParams.set('playerName', params.playerName);
    if (params.status) {
        const statuses = Array.isArray(params.status) ? params.status : [params.status];
        const normalized = statuses.map((status) => status.trim()).filter(Boolean);
        if (normalized.length > 0) {
            searchParams.set('status', normalized.join(','));
        }
    }
    if (params.search)
        searchParams.set('search', params.search);
    if (params.page)
        searchParams.set('page', String(params.page));
    if (params.limit)
        searchParams.set('limit', String(params.limit));
    if (params.sortBy)
        searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder)
        searchParams.set('sortOrder', params.sortOrder);
    const query = searchParams.toString();
    return `/api/admin/bookings${query ? `?${query}` : ''}`;
};
export const adminListBookings = (params = {}) => getJson(buildAdminBookingsPath(params));
export const adminGetBooking = (bookingId) => getJson(`/api/admin/bookings/${encodeURIComponent(bookingId)}`);
export const adminCreateBooking = (payload) => postJson('/api/admin/bookings', payload);
export const adminUpdateBooking = (bookingId, payload) => patchJson(`/api/admin/bookings/${encodeURIComponent(bookingId)}`, payload);
export const adminCancelBooking = (bookingId, reason, refundMethod) => {
    const params = new URLSearchParams();
    if (reason)
        params.set('reason', reason);
    if (refundMethod)
        params.set('refundMethod', refundMethod);
    const query = params.toString() ? `?${params.toString()}` : '';
    return delJson(`/api/admin/bookings/${encodeURIComponent(bookingId)}${query}`);
};
export const adminGetBookingCheckInStatus = (bookingId) => getJson(`/api/admin/bookings/${encodeURIComponent(bookingId)}/check-in`);
export const adminCheckInBooking = (bookingId, payload) => postJson(`/api/admin/bookings/${encodeURIComponent(bookingId)}/check-in`, payload ?? {});
export const adminUndoCheckInBooking = (bookingId) => delJson(`/api/admin/bookings/${encodeURIComponent(bookingId)}/check-in`);
export const adminGetBookingQr = (bookingId) => getJson(`/api/admin/bookings/${encodeURIComponent(bookingId)}/qr`);
export const getAdminBookingQrSvgUrl = (bookingId) => `${baseUrl()}/api/admin/bookings/${encodeURIComponent(bookingId)}/qr.svg`;
/* ------------------------------------------------------------------
 * Admin – Booking Policies (TeeTime backend)
 * -----------------------------------------------------------------*/
export const adminGetBookingCancellationPolicy = (clubId) => getJson(`/api/admin/booking-policy/cancellation/${encodeURIComponent(clubId)}`);
export const adminUpdateBookingCancellationPolicy = (clubId, payload) => putJson(`/api/admin/booking-policy/cancellation/${encodeURIComponent(clubId)}`, payload);
export const adminGetBookingRefundPolicy = (clubId) => getJson(`/api/admin/booking-policy/refund/${encodeURIComponent(clubId)}`);
export const adminUpdateBookingRefundPolicy = (clubId, payload) => putJson(`/api/admin/booking-policy/refund/${encodeURIComponent(clubId)}`, payload);
// Forecast (24h)
export const getWeatherForecast = (date, location) => getJson(`/api/weather/forecast?date=${encodeURIComponent(date)}&location=${encodeURIComponent(location)}`);
// Current
export const getWeatherCurrent = (location) => getJson(`/api/weather/current?location=${encodeURIComponent(location)}`);
// Hourly (next 24h) – optional start
export const getWeatherHourly = (location, start) => getJson(`/api/weather/hourly?location=${encodeURIComponent(location)}${start ? `&start=${encodeURIComponent(start)}` : ''}`);
// Daily multi-day outlook
export const getWeatherDaily = (location, start, days) => getJson(`/api/weather/daily?location=${encodeURIComponent(location)}&start=${encodeURIComponent(start)}&days=${encodeURIComponent(String(days))}`);
// Course forecast (24h)
export const getCourseForecast = (courseId, date) => getJson(`/api/weather/courses/${encodeURIComponent(String(courseId))}/forecast?date=${encodeURIComponent(date)}`);
// Alias endpoint
export const getCourseForecastAlias = (courseId, date) => getJson(`/api/weather/course/${encodeURIComponent(String(courseId))}?date=${encodeURIComponent(date)}`);
// Batch forecast (POST)
export const postWeatherBatch = (items) => postJson('/api/weather/batch', { items });
// Forward geocoding helper
export const geocodePlace = (place) => getJson(`/api/weather/geocode?place=${encodeURIComponent(place)}`);
const finiteNumberOr = (value, fallback) => typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const isCourseWeatherResponse = (value) => Boolean(value.current &&
    'temperature' in value.current &&
    Array.isArray(value.hourly) &&
    typeof value.playabilityScore === 'number' &&
    value.playabilityLabel);
const normalizeCourseWeatherHour = (entry, date, fallbackScore) => {
    const rawTime = entry.time ?? entry.hour ?? '';
    const time = date && /^\d{1,2}:\d{2}/.test(rawTime)
        ? `${date}T${rawTime.padStart(5, '0')}:00.000Z`
        : rawTime;
    const temperature = finiteNumberOr(entry.temperature ?? entry.temp, 0);
    return {
        time,
        temperature,
        precipitationChance: finiteNumberOr(entry.precipitationChance ?? entry.precipitation, 0),
        windSpeed: finiteNumberOr(entry.windSpeed, 0),
        playabilityScore: finiteNumberOr(entry.playabilityScore, fallbackScore),
    };
};
export const normalizeCourseWeatherResponse = (value, date) => {
    if (isCourseWeatherResponse(value)) {
        return value;
    }
    const playabilityScore = finiteNumberOr(value.playabilityScore, 0);
    const current = value.current ?? {};
    const temperature = finiteNumberOr(current.temperature ?? current.temp, 0);
    const hourly = value.hourly ?? value.hourlyForecast ?? [];
    return {
        playabilityScore,
        playabilityLabel: value.playabilityLabel ?? 'fair',
        current: {
            temperature,
            feelsLike: finiteNumberOr(current.feelsLike, temperature),
            windSpeed: finiteNumberOr(current.windSpeed, 0),
            windDirection: current.windDirection ?? '',
            humidity: finiteNumberOr(current.humidity, 0),
            uvIndex: finiteNumberOr(current.uvIndex, 0),
            condition: current.condition ?? 'Forecast',
        },
        hourly: hourly.map((entry) => normalizeCourseWeatherHour(entry, date, playabilityScore)),
        alerts: value.alerts ?? [],
        lastUpdated: value.lastUpdated ?? new Date().toISOString(),
    };
};
/**
 * Get weather with playability score for a course.
 * Includes alerts and hourly forecast with per-hour playability.
 */
export const getCourseWeather = (courseId, date, signal) => {
    const params = date ? `?date=${encodeURIComponent(date)}` : '';
    return getJson(`/api/v1/courses/${encodeURIComponent(courseId)}/weather${params}`, undefined, { signal }).then((response) => normalizeCourseWeatherResponse(response, date));
};
/**
 * Fetch courses for a club by clubCode.
 * Returns course UUIDs for use with reviews and other course-specific APIs.
 */
export const getClubCourses = (clubCode, signal) => getJson(`/api/clubs/${encodeURIComponent(clubCode)}/courses`, undefined, {
    signal,
});
export const getCompetitionLeaderboard = (competitionId, options = {}) => {
    const params = new URLSearchParams();
    if (options.divisionId)
        params.set('divisionId', options.divisionId);
    const query = params.toString();
    return getJson(`/api/competitions/${encodeURIComponent(competitionId)}/leaderboard${query ? `?${query}` : ''}`, undefined, { signal: options.signal });
};
export const getCompetitionDraw = (competitionId, options = {}) => getJson(`/api/competitions/${encodeURIComponent(competitionId)}/draw`, undefined, { signal: options.signal });
export const getClubCompetitions = (options = {}) => {
    const params = new URLSearchParams();
    if (options.clubId)
        params.set('clubId', options.clubId);
    if (options.status) {
        const statuses = Array.isArray(options.status) ? options.status : [options.status];
        statuses.forEach((s) => params.append('status', s));
    }
    const query = params.toString();
    return getJson(`/api/competitions${query ? `?${query}` : ''}`, undefined, { signal: options.signal });
};
export const getCompetitionDetail = (competitionId, signal) => getJson(`/api/competitions/${encodeURIComponent(competitionId)}`, undefined, {
    signal,
});
export const getCompetitionEntries = (competitionId, signal) => getJson(`/api/competitions/${encodeURIComponent(competitionId)}/entries`, undefined, { signal });
export const enterCompetition = async (competitionId, input, accessToken) => {
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken)
        headers['Authorization'] = `Bearer ${accessToken}`;
    const response = await fetch(buildUrl(`/api/competitions/${encodeURIComponent(competitionId)}/entries`), {
        method: 'POST',
        headers,
        body: JSON.stringify(input),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
            success: false,
            message: errorData.message || 'Failed to enter competition',
        };
    }
    const data = (await response.json());
    return { success: true, entryId: data.id };
};
export const withdrawFromCompetition = async (entryId, accessToken) => {
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken)
        headers['Authorization'] = `Bearer ${accessToken}`;
    const response = await fetch(buildUrl(`/api/competitions/entries/${encodeURIComponent(entryId)}/withdraw`), {
        method: 'POST',
        headers,
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
            success: false,
            message: errorData.message || 'Failed to withdraw',
        };
    }
    return { success: true };
};
export const getPlayerCompetitionEntry = async (competitionId, playerId, signal) => {
    const entries = await getCompetitionEntries(competitionId, signal);
    return entries.find((e) => e.playerId === playerId && !e.isWithdrawn) ?? null;
};
export const getCourseGpsCoordinates = (courseId, signal) => getJson(`/courses/${encodeURIComponent(courseId)}/gps/coordinates`, undefined, { signal });
export const getHoleGpsCoordinates = (courseId, hole, signal) => getJson(`/courses/${encodeURIComponent(courseId)}/gps/coordinates/${hole}`, undefined, { signal });
export const getCoursePinPositions = (courseId, date, signal) => {
    const params = date ? `?date=${encodeURIComponent(date)}` : '';
    return getJson(`/courses/${encodeURIComponent(courseId)}/gps/pin-positions${params}`, undefined, { signal });
};
export const getCourseDistances = (courseId, options) => {
    const params = new URLSearchParams();
    params.set('latitude', String(options.latitude));
    params.set('longitude', String(options.longitude));
    if (options.hole != null)
        params.set('hole', String(options.hole));
    if (options.date)
        params.set('date', options.date);
    return getJson(`/courses/${encodeURIComponent(courseId)}/gps/distance?${params.toString()}`, undefined, { signal: options.signal });
};
export const getCourseGreenCoordinates = (courseId, signal) => getJson(`/courses/${encodeURIComponent(courseId)}/gps/greens`, undefined, {
    signal,
});
export const getCourseHazardCoordinates = (courseId, signal) => getJson(`/courses/${encodeURIComponent(courseId)}/gps/hazards`, undefined, {
    signal,
});
export const adminListCourseReviews = (courseId, options = {}) => {
    const params = new URLSearchParams();
    if (options.status) {
        params.set('status', options.status);
    }
    const query = params.toString();
    return getJson(`/api/admin/courses/${encodeURIComponent(courseId)}/reviews${query ? `?${query}` : ''}`, undefined, { signal: options.signal });
};
export const adminModerateReview = (courseId, reviewId, request) => postJson(`/api/admin/courses/${encodeURIComponent(courseId)}/reviews/${encodeURIComponent(reviewId)}/moderate`, request);
export const adminGetReviewModerationSettings = (clubId) => {
    const params = new URLSearchParams();
    if (clubId)
        params.set('clubId', clubId);
    const query = params.toString();
    return getJson(`/api/admin/reviews/settings${query ? `?${query}` : ''}`);
};
export const adminGetReviewModerationSettingsResolved = (clubId) => {
    const params = new URLSearchParams();
    if (clubId)
        params.set('clubId', clubId);
    const query = params.toString();
    return getJson(`/api/admin/reviews/settings/resolved${query ? `?${query}` : ''}`);
};
export const adminUpdateReviewModerationSettings = (request) => postJson('/api/admin/reviews/settings', request);
export const adminGetReviewRequestSettings = (clubId) => {
    const params = new URLSearchParams({ clubId });
    return getJson(`/api/admin/reviews/request-settings?${params.toString()}`);
};
export const adminUpdateReviewRequestSettings = (request) => postJson('/api/admin/reviews/request-settings', request);
/**
 * Fetch approved course reviews with summary statistics.
 * @param courseId - The course UUID
 * @param options - Pagination and abort options
 */
export const listCourseReviews = (courseId, options = {}) => {
    const params = new URLSearchParams();
    if (options.limit !== undefined)
        params.set('limit', String(options.limit));
    if (options.cursor)
        params.set('cursor', options.cursor);
    const query = params.toString();
    return getJson(`/api/v1/courses/${encodeURIComponent(courseId)}/reviews${query ? `?${query}` : ''}`, undefined, { signal: options.signal });
};
/**
 * Get just the summary (count + average + dimensions) for a course.
 * Uses the list endpoint but extracts only the summary.
 */
export const getCourseReviewSummary = async (courseId, signal) => {
    const result = await listCourseReviews(courseId, { limit: 0, signal });
    return result.summary;
};
/**
 * Fetch review summaries for multiple courses in a single request.
 * @param courseIds - Array of course UUIDs
 */
export const getCourseReviewSummaries = (courseIds, signal) => postJson('/api/v1/courses/reviews/summary/batch', { courseIds }, undefined, { signal });
/**
 * Submit a new course review.
 * @param courseId - The course UUID
 * @param review - Review data
 */
export const createCourseReview = (courseId, review) => postJson(`/api/v1/courses/${encodeURIComponent(courseId)}/reviews`, review);
/**
 * Mark a review as helpful (increments helpful count).
 */
export const markReviewHelpful = (courseId, reviewId) => postJson(`/api/v1/courses/${encodeURIComponent(courseId)}/reviews/${encodeURIComponent(reviewId)}/helpful`, {});
/**
 * Report a review for moderation.
 */
export const reportReview = (courseId, reviewId, request) => postJson(`/api/v1/courses/${encodeURIComponent(courseId)}/reviews/${encodeURIComponent(reviewId)}/report`, request);
/**
 * Fetch pending review prompts for the current player.
 * Returns bookings eligible for review (completed, paid, no review, not dismissed).
 */
export const fetchPendingReviewPrompts = (signal) => getJson('/api/v1/players/me/pending-reviews', undefined, { signal });
/**
 * Dismiss a pending review prompt so it doesn't show again.
 */
export const dismissReviewPrompt = (bookingId) => postJson(`/api/v1/players/me/pending-reviews/${encodeURIComponent(bookingId)}/dismiss`, {});
/**
 * Get CSAT/NPS analytics with trends (admin endpoint).
 */
export const getSurveyAnalytics = (options = {}) => {
    const params = new URLSearchParams();
    if (options.courseId)
        params.set('courseId', options.courseId);
    if (options.clubId)
        params.set('clubId', options.clubId);
    if (options.startDate)
        params.set('startDate', options.startDate);
    if (options.endDate)
        params.set('endDate', options.endDate);
    const query = params.toString();
    return getJson(`/api/admin/surveys/analytics${query ? `?${query}` : ''}`, undefined, { signal: options.signal });
};
/**
 * Fetch a player's current handicap information.
 */
export const getPlayerHandicap = (playerId, options = {}) => playerId
    ? getJson(`/api/v1/players/me/handicap`, undefined, {
        signal: options.signal,
    }).catch(() => null)
    : Promise.resolve(null);
/**
 * Fetch a player's handicap history (snapshots and postings).
 */
export const getHandicapHistory = (playerId, limit = 20, options = {}) => playerId
    ? getJson(`/api/v1/players/me/handicap/history?limit=${limit}`, undefined, { signal: options.signal }).catch(() => null)
    : Promise.resolve(null);
/**
 * Fetch course rating/slope/par for a provider course/tee.
 */
export const getCourseTeeInfo = (params, options = {}) => {
    const query = new URLSearchParams();
    query.set('provider', params.provider);
    query.set('associationCourseId', params.associationCourseId);
    if (params.teeColour) {
        query.set('teeColour', params.teeColour);
    }
    return getJson(`/api/associations/course-tee-info?${query.toString()}`, undefined, {
        signal: options.signal,
    });
};
/**
 * Refresh handicap from association (triggers API call to provider).
 */
export const refreshHandicapFromAssociation = (playerId, provider) => playerId
    ? postJson(`/api/v1/players/me/handicap/refresh`, { provider })
    : Promise.reject(new Error('Missing playerId'));
/**
 * Record a manual handicap score entry for personal tracking.
 */
export const createManualHandicapPosting = (playerId, payload) => playerId
    ? postJson(`/api/v1/players/me/handicap/manual`, payload)
    : Promise.reject(new Error('Missing playerId'));
/**
 * Calculate course handicap for a specific course layout.
 * WHS Formula: CH = HI × (SR/113) + (CR - Par)
 */
export const calculateCourseHandicap = (handicapIndex, slopeRating, courseRating, par) => {
    const ch = handicapIndex * (slopeRating / 113) + (courseRating - par);
    return Math.round(ch);
};
/**
 * Fetch the current player's wallet balance and recent transactions.
 */
export const fetchPlayerWallet = (options = {}) => getJson('/api/v1/players/me/wallet', undefined, { signal: options.signal }).catch(() => null);
/**
 * Fetch the current player's wallet balance without transaction history.
 */
export const fetchPlayerWalletSummary = (options = {}) => getJson('/api/v1/players/me/wallet/summary', undefined, {
    signal: options.signal,
}).catch(() => null);
/**
 * Fetch additional wallet transactions with pagination.
 */
export const fetchWalletTransactions = (options = {}) => {
    const params = new URLSearchParams();
    if (options.limit !== undefined)
        params.set('limit', String(options.limit));
    if (options.offset !== undefined)
        params.set('offset', String(options.offset));
    const query = params.toString();
    return getJson(`/api/v1/players/me/wallet/transactions${query ? `?${query}` : ''}`, undefined, { signal: options.signal }).catch(() => []);
};
/**
 * Reschedule an existing booking to a new slot.
 * Note: The new slot must belong to the same club/tenant.
 * @param bookingId - The booking ID to reschedule
 * @param options - New slot ID and optional settings
 */
export const rescheduleBooking = (bookingId, options) => {
    const body = {
        newSlotId: options.newSlotId,
        playerType: options.playerType,
        notifyPlayers: options.notifyPlayers,
    };
    return patchJson(`/api/bookings/${encodeURIComponent(bookingId)}/reschedule`, body);
};
export const rescheduleBookingWithMeta = (bookingId, options) => {
    const body = {
        newSlotId: options.newSlotId,
        playerType: options.playerType,
        notifyPlayers: options.notifyPlayers,
    };
    return patchJsonWithMeta(`/api/bookings/${encodeURIComponent(bookingId)}/reschedule`, body, undefined, toBookingResponseMeta);
};
/**
 * Get player's scorecards for a competition
 */
export const getPlayerScorecards = (competitionId, signal) => {
    return getJson(`/competitions/${encodeURIComponent(competitionId)}/my-scorecards`, undefined, { signal });
};
/**
 * Get scorecard detail by ID
 */
export const getScorecardDetail = (scorecardId, signal) => {
    return getJson(`/competitions/scorecards/${encodeURIComponent(scorecardId)}`, undefined, { signal });
};
/**
 * Update hole scores on a scorecard
 */
export const updateScorecard = (scorecardId, data, signal) => {
    return putJson(`/competitions/scorecards/${encodeURIComponent(scorecardId)}`, data, undefined, { signal });
};
/**
 * Submit scorecard for review
 */
export const submitScorecard = (scorecardId, _signal) => {
    return postJson(`/competitions/scorecards/${encodeURIComponent(scorecardId)}/submit`, {});
};
// Compat: some tests expect a BookingError class from the client package
export class BookingError extends Error {
}
export const fetchNotificationPreferences = () => getJson('/api/players/me/notification-preferences');
export const saveNotificationPreferences = (prefs) => putJson('/api/players/me/notification-preferences', prefs);
//# sourceMappingURL=index.js.map